// src/hooks/usePDFGenerator.ts
import { useCallback } from "react";
import jsPDF from "jspdf";
import { PDF_COLORS, PDF_FONTS, PDF_MARGINS } from "../constants/constants";

export const usePDFGenerator: () => (
  headerData: any,
  contentData: any
) => void = () => {
  const generatePDF = useCallback((headerData: any, contentData: any) => {
    if (!contentData || !headerData) return;

    const doc = new jsPDF();
    let yPosition = PDF_MARGINS.top;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = PDF_MARGINS.left;
    let pageNumber = 1;

    // Construct full name with proper fallback logic
    const firstName = headerData.basicInfo?.firstName;
    const lastName = headerData.basicInfo?.lastName;
    const fullName =
      firstName || lastName
        ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
        : "Client";

    // Header function
    const addHeader = (): void => {
      doc.setFontSize(PDF_FONTS.title);
      doc.setTextColor(PDF_COLORS.primary);
      doc.setFont("helvetica", "bold");
      doc.text(`${fullName}'s Tax Organizer`, margin, 20);

      doc.setFontSize(PDF_FONTS.body);
      doc.setTextColor(PDF_COLORS.secondary);
      doc.text(
        `Generated: ${new Date().toLocaleDateString()}`,
        pageWidth - margin - 40,
        20
      );

      doc.setLineWidth(0.5);
      doc.line(margin, 25, pageWidth - margin, 25);
    };

    // Footer function
    const addFooter = () => {
      doc.setFontSize(PDF_FONTS.body);
      doc.setTextColor(PDF_COLORS.secondary);
      doc.text(
        `Page ${pageNumber}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    };

    // Helper function to format currency values
    const formatCurrency = (value: number) => {
      return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Helper function to format date values
    const formatDate = (value: Date | string) => {
      const date = new Date(value);
      return isNaN(date.getTime())
        ? value
        : date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
    };

    // Format field value based on type
    const formatFieldValue = (field: any) => {
      if (
        field.value === null ||
        field.value === undefined ||
        field.value === ""
      ) {
        return "N/A";
      }

      // Handle boolean values
      if (typeof field.value === "boolean") {
        return field.value ? "Yes" : "No";
      }

      // Handle array values
      if (Array.isArray(field.value)) {
        return field.value.length > 0 ? field.value.join(", ") : "None";
      }

      // Handle object values
      if (typeof field.value === "object" && !(field.value instanceof Date)) {
        return JSON.stringify(field.value);
      }

      // Format numbers as currency if applicable
      if (typeof field.value === "number") {
        const isCurrency = [
          "amount",
          "paid",
          "revenue",
          "tuition",
          "income",
          "tax",
          "deduction",
          "total",
        ].some((key) => field.label.toLowerCase().includes(key));
        return isCurrency
          ? formatCurrency(field.value)
          : field.value.toLocaleString();
      }

      // Format dates
      if (
        field.value instanceof Date ||
        (typeof field.value === "string" &&
          (field.label.toLowerCase().includes("date") ||
            field.key?.toLowerCase().includes("date")))
      ) {
        return formatDate(field.value);
      }

      return field.value.toString();
    };

    // Process items array (for repeating sections like dependents, residences, etc.)
    const processItems = (items: any[], y: number, depth: number): number => {
      if (!items || items.length === 0) return y;

      items.forEach((item, index) => {
        y = processSection(item, y, depth);

        // Add some spacing between items but not after the last one
        if (index < items.length - 1) {
          y += 5;
        }
      });

      return y;
    };

    // Recursive function to process sections and fields
    const processSection = (
      section: any,
      y: number,
      depth: number = 0
    ): number => {
      if (y > doc.internal.pageSize.getHeight() - 40) {
        addFooter();
        doc.addPage();
        pageNumber++;
        y = PDF_MARGINS.top;
        addHeader();
      }

      const indentation = margin + depth * 10;
      const sectionColors = [
        PDF_COLORS.primary,
        PDF_COLORS.accent,
        PDF_COLORS.secondary,
      ];

      // Skip empty sections
      if (
        !section.label &&
        !section.fields &&
        !section.items &&
        Object.keys(section).length <= 1
      ) {
        return y;
      }

      // Section title styling
      if (section.label) {
        doc.setFont("helvetica", depth === 0 ? "bold" : "normal");
        doc.setFontSize(PDF_FONTS.section - depth * 2);
        doc.setTextColor(sectionColors[depth] || PDF_COLORS.primary);
        doc.text(section.label, indentation, y);
        y += 8;

        // Underline for main sections
        if (depth === 0) {
          doc.setLineWidth(0.3);
          doc.line(indentation, y - 2, indentation + 50, y - 2);
          y += 5;
        }
      }

      // Process items if available (for repeating sections)
      if (section.items) {
        y = processItems(section.items, y, depth + 1);
      }

      // Process fields if available
      if (section.fields) {
        doc.setFontSize(PDF_FONTS.body);
        doc.setTextColor(PDF_COLORS.text);
        doc.setFont("helvetica", "normal");

        Object.entries(section.fields).forEach(
          ([key, field]: [string, any]) => {
            if (y > doc.internal.pageSize.getHeight() - 40) {
              addFooter();
              doc.addPage();
              pageNumber++;
              y = PDF_MARGINS.top;
              addHeader();
            }

            // Skip fields with no label or value (unless explicitly included)
            if (!field.label && field.value === undefined) {
              return;
            }

            const formattedValue = formatFieldValue(field);
            const displayLabel = field.label || key;

            // Render field label and value
            doc.setFont("helvetica", "bold");
            doc.text(`${displayLabel}:`, indentation + 5, y);

            doc.setFont("helvetica", "normal");
            const text = doc.splitTextToSize(
              formattedValue,
              pageWidth - indentation - margin - 10
            );
            doc.text(text, indentation + 35, y);

            y += Math.max(8, text.length * 7);
          }
        );
        y += 10;
      }

      // Process any nested subsections (excluding special keys)
      const excludedKeys = ["key", "label", "fields", "items", "reviewMode"];
      Object.keys(section).forEach((key) => {
        if (excludedKeys.includes(key)) return;
        const subsection = section[key];
        if (subsection && typeof subsection === "object") {
          y = processSection(subsection, y, depth + 1);
        }
      });

      return y;
    };

    // Generate PDF by processing all content sections
    addHeader();
    Object.values(contentData).forEach((section: any) => {
      yPosition = processSection(section, yPosition);
      yPosition += 15;
    });

    addFooter();

    // Use fullName for filename, replacing spaces with underscores
    const fileNameBase = fullName !== "Client" ? fullName : "Client";
    const fileName =
      `${fileNameBase}_Tax_Organizer_${new Date().toISOString().slice(0, 10)}.pdf`
        .replace(/\s+/g, "_")
        .toLowerCase();

    doc.save(fileName);
  }, []);

  return generatePDF;
};

export default usePDFGenerator;
